import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import * as styles from "./device-select-panel.module.scss";
import PopupSheetTableHeader from "../popup-sheet/popup-sheet-table-header";
import PopupSheetContent from "../popup-sheet/popup-sheet-content";
import PopupSheetContentLoading from "../popup-sheet/popup-sheet-content-loading";
import { VelavuDevice } from "velavu-js-api";
import { StyleableIcon } from "../../helper/icon-helper";
import VelavuButton from "../velavu-button";
import FilterableDevicesSource from "./filterable-devices-source";
import { useDebouncedEffect } from "../../helper/hook-helper";
import VelavuInput from "../velavu-input";
import IconSearch from "../../dynamicicons/icon-search";
import { EMPTY_STATE_ICON_TAG } from "../empty-state-icon/empty-state-icon-sources";
import EmptyState from "../empty-state/empty-state";
import PopupSheet from "../popup-sheet/popup-sheet";
import PopupSheetHeader from "../popup-sheet/popup-sheet-header";
import PopupSheetContentEmpty from "../popup-sheet/popup-sheet-content-empty";
import PopupSheetFloatingButton from "../popup-sheet/popup-sheet-floating-button";
import DeviceSelectPanelEntry from "./device-select-panel-entry";
import PopupSheetContentList from "../popup-sheet/popup-sheet-content-list";
import whitelabel from "../../whitelabel";
import { VelavuTableHeaderLabel } from "../velavu-table-header";

export interface DeviceSelectPanelProps {
	style?: React.CSSProperties;
	className?: string;

	// The title of the panel
	title: string;
	// A source from which to load devices to show in the list
	devicesSource: FilterableDevicesSource;
	// Whether to show an asset information column in the list
	showAssetInformation?: boolean;
	// Optional callback that returns a warning message for a device, if any
	getWarningDetails?: (device: VelavuDevice) => string | undefined;
	// A callback invoked if the user closes the panel
	onClose?: () => void;
	// The text label to show on the confirm button
	buttonLabel?: string;
	// The icon to show on the confirm button
	buttonIcon?: StyleableIcon;
	// A callback invoked if the user confirms the selection of a device
	onSelect?: (device: VelavuDevice) => void;
}

/**
 * A popup panel that allows the user to confirm the selection of a device
 */
export default function DeviceSelectPanel(props: DeviceSelectPanelProps) {
	const { t } = useTranslation();
	const [filterInput, setFilterInput] = useState<string>("");
	const sanitizedFilterInput = useMemo(() => {
		const trimmedInput = filterInput.trim();
		if (trimmedInput.length !== 0) {
			return trimmedInput;
		} else {
			return null;
		}
	}, [filterInput]);

	const [devices, setDevices] = useState<VelavuDevice[] | null>(null);
	useDebouncedEffect(
		() => {
			setDevices(null);
			props
				.devicesSource(sanitizedFilterInput)
				.then(setDevices)
				.catch((error) =>
					console.warn("Failed to load devices", error),
				);
		},
		300,
		true,
		[setDevices, sanitizedFilterInput, props.devicesSource],
	);

	const [selectedID, setSelectedID] = useState<string | null>(null);
	const selectedDevice = useMemo(() => {
		if (selectedID === null || devices === null) {
			return null;
		}

		return (
			devices.find((device) => {
				return device.id === selectedID;
			}) ?? null
		);
	}, [selectedID, devices]);

	const propsOnSelect = props.onSelect;
	const confirm = useCallback(() => {
		if (selectedDevice === null) {
			return;
		}
		propsOnSelect?.(selectedDevice);
	}, [selectedDevice, propsOnSelect]);

	return (
		<PopupSheet style={props.style} className={props.className}>
			<PopupSheetHeader title={props.title} onClose={props.onClose} />

			<VelavuInput
				className={styles.searchBar}
				value={filterInput}
				onChange={setFilterInput}
				placeholder={t("elements.device_select.search_placeholder")}
				leadingIcon={
					<IconSearch
						size={24}
						color={whitelabel.palette.secondary900}
					/>
				}
			/>

			<PopupSheetTableHeader>
				<VelavuTableHeaderLabel sizing={{ width: 110 }}>
					{t("elements.device_select.device_id")}
				</VelavuTableHeaderLabel>
				<VelavuTableHeaderLabel
					sizing={
						props.showAssetInformation ? { width: 60 } : { flex: 1 }
					}
					alignment={props.showAssetInformation ? "center" : "start"}
				>
					{t("elements.device_select.type")}
				</VelavuTableHeaderLabel>
				{props.showAssetInformation && (
					<VelavuTableHeaderLabel sizing={{ flex: 1 }}>
						{t("elements.device_select.asset")}
					</VelavuTableHeaderLabel>
				)}
			</PopupSheetTableHeader>

			{devices === null ? (
				<PopupSheetContent>
					<PopupSheetContentLoading />
				</PopupSheetContent>
			) : devices.length === 0 ? (
				<PopupSheetContent>
					<PopupSheetContentEmpty>
						<EmptyState
							src={EMPTY_STATE_ICON_TAG}
							title={t("elements.device_select.empty_title")}
							subtitle={t(
								"elements.device_select.empty_subtitle",
							)}
						/>
					</PopupSheetContentEmpty>
				</PopupSheetContent>
			) : (
				<PopupSheetContent>
					<PopupSheetContentList>
						{devices.map((device) => (
							<DeviceSelectPanelEntry
								key={device.id}
								device={device}
								selected={device.id === selectedID}
								showAssetInformation={
									props.showAssetInformation
								}
								warning={props.getWarningDetails?.(device)}
								onClick={() => setSelectedID(device.id)}
							/>
						))}
					</PopupSheetContentList>
				</PopupSheetContent>
			)}

			{selectedDevice !== null && (
				<PopupSheetFloatingButton>
					<VelavuButton
						label={props.buttonLabel}
						icon={props.buttonIcon}
						onClick={confirm}
					/>
				</PopupSheetFloatingButton>
			)}
		</PopupSheet>
	);
}
